(() => {
  if (customElements.get('yarn-product-features') || !window.KickisYarnSpecs) return;
  const Core = window.KickisYarnSpecs, storageKey = 'kickis-yarn-comparison-dev-v1';
  const escape = value => String(value ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  const locale = document.documentElement.lang || 'sv';
  const format = n => Number(n).toLocaleString(locale, {maximumFractionDigits: 1});
  const productUrl = p => {
    const url = new URL((window.Shopify?.routes?.root || '/') + 'products/' + encodeURIComponent(p.handle), location.origin);
    try {
      const variant = new URL(p.url, location.origin).searchParams.get('variant');
      if (/^\d+$/.test(variant || '')) url.searchParams.set('variant', variant);
    } catch {}
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview && /^\d+$/.test(preview)) url.searchParams.set('preview_theme_id', preview);
    return url.href;
  };
  const photo = p => {
    try {
      const u = new URL(p.image, location.origin);
      return u.protocol === 'https:' && (u.origin === location.origin || u.hostname === 'cdn.shopify.com' || u.hostname.endsWith('.myshopify.com')) ? `<img src="${escape(u.href)}" alt="${escape(p.title)}" width="480" height="480" loading="lazy">` : '';
    } catch { return ''; }
  };
  let catalogPromise;
  async function catalog(endpoint) {
    if (!catalogPromise) catalogPromise = (async () => {
      const products = new Map(); let pages = 1;
      for (let page = 1; page <= pages; page++) {
        const url = new URL(endpoint, location.origin);
        if (url.origin !== location.origin) throw Error('Invalid catalog origin');
        url.searchParams.set('page', String(page));
        const preview = new URL(location.href).searchParams.get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        const response = await fetch(url, {cache: 'no-store', signal: AbortSignal.timeout(15000)});
        if (!response.ok) throw Error('Catalog failed');
        const data = await response.json();
        if (!Array.isArray(data.products) || !Number.isInteger(data.pages) || data.pages < 0 || data.pages > 50) throw Error('Invalid catalog');
        pages = data.pages;
        for (const p of data.products) if (Core.selection([p.handle]).length) products.set(p.handle, Core.specification(p));
      }
      return [...products.values()];
    })().catch(error => {catalogPromise = null; throw error;});
    return catalogPromise;
  }
  class YarnProductFeatures extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      try {
        this.current = Core.specification(JSON.parse(this.querySelector('[data-yarn-current]').textContent));
        this.labels = JSON.parse(this.querySelector('[data-yarn-labels]').textContent);
      } catch { return; }
      this.ready = true; this.selected = []; this.allowCompare = this.dataset.compare === 'true';
      if (this.allowCompare) {
        try { this.selected = Core.selection(JSON.parse(localStorage.getItem(storageKey) || '[]')); } catch {}
        const open = this.querySelector('[data-yarn-open]');
        if (open) { open.hidden = false; open.addEventListener('click', () => this.openComparison()); }
      }
      this.renderSpecs();
      document.dispatchEvent(new CustomEvent('kickis:yarn-features-ready'));
    }
    disconnectedCallback() {
      this.dialog?.remove(); this.dialog = null;
      for (const table of this.replacedTables || []) table.removeAttribute('data-yarn-replaced');
      this.ready = false;
    }
    t(key) { return this.labels[key] || key; }
    money(p) {
      if (!Number.isFinite(p.price) || p.price < 0 || !/^[A-Z]{3}$/.test(p.currency || '')) return this.t('missing');
      const amount = new Intl.NumberFormat(locale, {style:'currency', currency:p.currency}).format(p.price / 100);
      return (p.priceVaries ? this.t('from') + ' ' : '') + amount;
    }
    fields(p) {
      return [
        ['fiber', p.fiber || (p.materials.length ? p.materials.join(', ') + ' (' + this.t('percent_missing') + ')' : '')],
        ['weight', p.weight ? format(p.weight) + ' g' : ''],
        ['meters', p.meters ? format(p.meters) + ' m' : ''],
        ['meter_weight', p.weight && p.meters ? format(p.meters / p.weight * 100) + ' m' : ''],
        ['gauge', p.gaugeText || (p.gauge ? format(p.gauge) + ' / 10 cm' : '')],
        ['needle', p.needle], ['hook', p.hook], ['care', p.care]
      ];
    }
    renderSpecs() {
      const root = this.querySelector('[data-yarn-specs]');
      const description = this.closest('product-info')?.querySelector('.product__description') || (this.previousElementSibling?.matches('.product__description') ? this.previousElementSibling : null);
      this.replacedTables = [...(description?.querySelectorAll('table') || [])].filter(table => Core.rows(table.outerHTML).filter(([label])=>Object.values(Core.labels).some(re=>re.test(label))).length >= 2);
      const extras = this.replacedTables.flatMap(table => Core.rows(table.outerHTML)).filter(([label])=>!Object.values(Core.labels).some(re=>re.test(label)));
      const known = this.fields(this.current).map(([key, value])=>[this.t(key), value]);
      root.innerHTML = `<table class="yarn-spec-table"><caption>${escape(this.t('specs'))}</caption><tbody>${known.concat(extras).map(([key, value])=>`<tr><th scope="row">${escape(key)}</th><td>${escape(value || this.t('missing'))}</td></tr>`).join('')}</tbody></table>`;
      root.hidden = false;
      for (const table of this.replacedTables) table.setAttribute('data-yarn-replaced', 'true');
    }
    async load() {
      if (!this.products) {
        this.products = await catalog(this.dataset.catalogUrl);
        const fresh = this.products.find(p=>p.handle === this.current.handle);
        if (fresh) this.current = fresh;
        else this.products.push(this.current);
      }
      return this.products;
    }
    persist() {
      try { localStorage.setItem(storageKey, JSON.stringify(this.selected)); } catch {}
    }
    async openComparison(handle) {
      if (!this.allowCompare) return;
      if (!this.dialog) this.makeDialog();
      this.opener = this.querySelector('[data-yarn-open]') || document.activeElement;
      if (!this.dialog.open) this.dialog.showModal();
      this.dialog.querySelector('[data-search]').value = '';
      this.closeSearch();
      this.message(this.t('loading'));
      try {
        await this.load();
        this.selected = this.selected.filter(h=>this.products.some(p=>p.handle === h));
        if (!this.selected.length) this.selected = [this.current.handle];
        if (handle && !this.selected.includes(handle)) {
          if (this.selected.length < 4) this.selected.push(handle);
          else this.message(this.t('limit'));
        }
        this.persist(); this.renderComparison(); this.renderSearch();
        if (this.dialog.querySelector('[data-dialog-message]').textContent === this.t('loading')) this.message('');
        if (this.dialog.open) this.dialog.querySelector(matchMedia('(max-width: 749px)').matches?'[data-close]':'[data-search]').focus({preventScroll:true});
      } catch { this.message(this.t('load_error'), true); }
    }
    makeDialog() {
      const dialog = document.createElement('dialog');
      this.dialog = dialog; dialog.className = 'yarn-comparison';
      dialog.setAttribute('closedby','closerequest');
      const id = 'yarn-compare-' + Math.random().toString(36).slice(2);
      dialog.setAttribute('aria-labelledby', id);
dialog.innerHTML = `<div class="yarn-comparison__header"><h2 id="${id}">${escape(this.t('compare'))}</h2><button type="button" class="button button--secondary" data-close>${escape(this.t('close'))}</button></div><p class="yarn-note">${escape(this.t('note'))}</p><div class="yarn-comparison__search-shell"><label for="${id}-search">${escape(this.t('search'))}</label><input class="yarn-comparison__search" id="${id}-search" type="search" role="combobox" aria-autocomplete="list" aria-expanded="false" aria-controls="${id}-results" data-search autocomplete="off"><div class="yarn-comparison__popup" data-search-popup hidden><p class="yarn-note" role="status" data-search-count></p><ul class="yarn-comparison__results" id="${id}-results" role="listbox" aria-label="${escape(this.t('search'))}" data-results></ul></div></div><p role="status" data-dialog-message></p><p role="status" data-selected-count></p><div class="yarn-comparison__table" tabindex="0" role="region" aria-label="${escape(this.t('compare'))}" data-compare-table></div><button type="button" class="button button--secondary" data-clear>${escape(this.t('clear'))}</button>`;
      document.body.append(dialog);
      const suggestions=document.createElement('details');
      suggestions.className='yarn-comparison__suggestions';suggestions.dataset.suggestions='';
      suggestions.innerHTML=`<summary>${escape(this.t('suggest_title'))}</summary><div class="yarn-comparison__suggestion-body"><label for="${id}-source">${escape(this.t('suggest_base'))}</label><select id="${id}-source" data-suggestion-source></select><p class="yarn-note" data-suggestion-note role="status"></p><ul data-suggestion-list></ul></div>`;
      dialog.querySelector('[data-selected-count]').before(suggestions);
      suggestions.addEventListener('toggle',()=>{if(suggestions.open){this.closeSearch();this.renderSuggestions();}});
      suggestions.querySelector('select').addEventListener('change',event=>{this.suggestionSource=event.target.value;this.renderSuggestions();});
      dialog.addEventListener('close', ()=>{if(this.opener?.isConnected)this.opener.focus();});
      dialog.addEventListener('keydown', event=>{
        if(event.key==='Escape' && !event.isComposing){
          event.preventDefault();event.stopPropagation();
          if(!dialog.querySelector('[data-search-popup]').hidden)this.closeSearch();
          else dialog.close();
        }
      });
      dialog.addEventListener('click', event=>{
        if (event.target.closest('[data-close]')) {dialog.close();return;}
        const option=event.target.closest('[data-add]');
        if(option){this.addComparison(option.dataset.add);return;}
        if(!event.target.closest('.yarn-comparison__search-shell'))this.closeSearch();
        const button = event.target.closest('button'); if (!button) return;
        if(button.dataset.suggestAdd){
          const handle=button.dataset.suggestAdd;
          if(!this.suggestionCandidates?.includes(handle))return;
          this.addComparison(handle);
          (dialog.querySelector('[data-suggest-add]:not(:disabled)')||dialog.querySelector('[data-suggestions] summary')).focus({preventScroll:true});
          return;
        }
        if (button.hasAttribute('data-retry')) {this.openComparison();return;}
        if (button.hasAttribute('data-clear')) this.selected = [];
        else if (button.dataset.remove) this.selected = this.selected.filter(h=>h !== button.dataset.remove);
        else return;
        this.persist(); this.message(''); this.renderComparison(); this.renderSearch();
        dialog.querySelector(matchMedia('(max-width: 749px)').matches?'[data-compare-table]':'[data-search]').focus({preventScroll:true});
      });
      const search=dialog.querySelector('[data-search]');
      search.addEventListener('input', ()=>this.renderSearch());
      search.addEventListener('focus', ()=>this.renderSearch());
      search.addEventListener('keydown', event=>{
        if(event.isComposing || event.altKey || event.ctrlKey || event.metaKey)return;
        if(event.key==='ArrowDown'||event.key==='ArrowUp'){
          event.preventDefault();
          if(dialog.querySelector('[data-search-popup]').hidden)this.renderSearch();
          const options=[...dialog.querySelectorAll('[data-add]:not([aria-disabled="true"])')];
          if(!options.length)return;
          const current=options.findIndex(option=>option.id===search.getAttribute('aria-activedescendant'));
          const next=current<0?(event.key==='ArrowDown'?0:options.length-1):Math.max(0,Math.min(options.length-1,current+(event.key==='ArrowDown'?1:-1)));
          this.highlightSearch(options[next]);
        }else if(event.key==='Enter'){
          event.preventDefault();
          const active=document.getElementById(search.getAttribute('aria-activedescendant'));
          if(!dialog.querySelector('[data-search-popup]').hidden && active?.dataset.add)this.addComparison(active.dataset.add);
        }
      });
      const results=dialog.querySelector('[data-results]');
      results.addEventListener('pointerdown', event=>{if(event.pointerType==='mouse'&&event.target.closest('[data-add]'))event.preventDefault();});
      results.addEventListener('pointermove', event=>{
        if(event.pointerType!=='mouse')return;
        const option=event.target.closest('[data-add]');
        if(option?.getAttribute('aria-disabled')!=='true')this.highlightSearch(option,false);
      });
      dialog.querySelector('.yarn-comparison__search-shell').addEventListener('focusout', event=>{
        if(event.relatedTarget&&!event.currentTarget.contains(event.relatedTarget))this.closeSearch();
      });
    }
    closeSearch() {
      this.dialog.querySelector('[data-search-popup]').hidden=true;
      const search=this.dialog.querySelector('[data-search]');
      search.setAttribute('aria-expanded','false');search.removeAttribute('aria-activedescendant');
    }
    highlightSearch(option, scroll=true) {
      if(!option)return;
      for(const item of this.dialog.querySelectorAll('[data-add]'))item.setAttribute('aria-selected',String(item===option));
      this.dialog.querySelector('[data-search]').setAttribute('aria-activedescendant',option.id);
      if(scroll){
        const list=this.dialog.querySelector('[data-results]'),box=option.getBoundingClientRect(),bounds=list.getBoundingClientRect();
        if(box.top<bounds.top)list.scrollTop-=bounds.top-box.top;
        else if(box.bottom>bounds.bottom)list.scrollTop+=box.bottom-bounds.bottom;
      }
    }
    addComparison(handle) {
      if(this.selected.length>=4){this.message(this.t('limit'));return;}
      if(this.selected.includes(handle)||!this.products.some(p=>p.handle===handle))return;
      this.selected.push(handle);this.persist();this.message('');this.renderComparison();
      const search=this.dialog.querySelector('[data-search]');search.value='';this.renderSearch();
      this.dialog.querySelector(matchMedia('(max-width: 749px)').matches?'[data-compare-table]':'[data-search]').focus({preventScroll:true});
    }
    message(text, retry = false) {
      if (!this.dialog) return;
      const node = this.dialog.querySelector('[data-dialog-message]');
      node.textContent = text;
      if (retry) {const button=document.createElement('button');button.type='button';button.dataset.retry='';button.textContent=this.t('retry');node.append(' ',button);}
    }
    renderSearch() {
      if (!this.products || !this.dialog) return;
      const search=this.dialog.querySelector('[data-search]'),list=this.dialog.querySelector('[data-results]'),count=this.dialog.querySelector('[data-search-count]');
      const query = search.value.trim().toLocaleLowerCase(locale);
      this.closeSearch();list.innerHTML='';count.textContent='';
      if(!query)return;
      const matches = this.products.filter(p=>!this.selected.includes(p.handle) && `${p.title} ${p.vendor || ''} ${p.fiber}`.toLocaleLowerCase(locale).includes(query)).sort((a,b)=>a.title.localeCompare(b.title,locale));
      count.textContent=this.selected.length>=4?this.t('limit'):matches.length?`${matches.length} ${this.t('search_results')}`:this.t('no_results');
      list.innerHTML = matches.map((p,index)=>`<li id="${list.id}-${index}" role="option" tabindex="-1" aria-selected="false" aria-disabled="${this.selected.length>=4}" data-add="${escape(p.handle)}" aria-label="${escape(this.t('add')+': '+p.title)}">${photo(p)}<span class="yarn-comparison__option-name">${escape(p.title)}<small>${escape(p.vendor || '')}</small></span><span class="yarn-comparison__option-add" aria-hidden="true">+</span></li>`).join('');
      list.scrollTop=0;
      this.dialog.querySelector('[data-search-popup]').hidden=false;search.setAttribute('aria-expanded','true');
    }
    renderComparison() {
      const selected = this.selected.map(h=>this.products.find(p=>p.handle===h)).filter(Boolean);
      this.dialog.querySelector('[data-selected-count]').textContent = selected.length + ' ' + this.t('selected');
      this.renderSuggestions();
      const root = this.dialog.querySelector('[data-compare-table]');
      root.style.setProperty('--yarn-count',String(Math.max(2,selected.length)));
      if (!selected.length) {root.innerHTML='';return;}
      const fields = selected.map(p=>this.fields(p).concat([['suitable',p.uses.join(' · ')],['price',this.money(p)],['stock',this.t(p.available?'in_stock':'sold_out')]]));
      root.innerHTML=`<table><caption class="visually-hidden">${escape(this.t('compare'))}</caption><thead><tr><th scope="col">${escape(this.t('specs'))}</th>${selected.map(p=>`<th scope="col"><button type="button" data-remove="${escape(p.handle)}" aria-label="${escape(this.t('remove') + ': ' + p.title)}" title="${escape(this.t('remove') + ': ' + p.title)}"><span aria-hidden="true">×</span></button><a href="${escape(productUrl(p))}">${photo(p)}${escape(p.title)}</a></th>`).join('')}</tr></thead><tbody>${fields[0].map(([key],i)=>{const values=fields.map(column=>column[i][1]||this.t('missing'));return `<tr><th scope="row">${escape(this.t(key))}</th>${values.map(value=>`<td class="${new Set(values).size>1?'yarn-difference':''}">${escape(value)}</td>`).join('')}</tr>`;}).join('')}</tbody></table>`;
    }
    renderSuggestions() {
      const panel=this.dialog?.querySelector('[data-suggestions]');
      if(!panel?.open||!this.products)return;
      const sources=this.selected.map(handle=>this.products.find(p=>p.handle===handle)).filter(Boolean);
      if(!sources.some(p=>p.handle===this.suggestionSource))this.suggestionSource=sources[0]?.handle||'';
      const select=panel.querySelector('[data-suggestion-source]'),note=panel.querySelector('[data-suggestion-note]'),list=panel.querySelector('[data-suggestion-list]');
      select.innerHTML=sources.map(p=>`<option value="${escape(p.handle)}">${escape(p.title)}</option>`).join('');
      select.value=this.suggestionSource;select.disabled=!sources.length;
      const source=sources.find(p=>p.handle===this.suggestionSource);
      const matches=source?this.products.filter(p=>!this.selected.includes(p.handle)).map(p=>({p,match:Core.similar(source,p)})).filter(item=>item.match).sort((a,b)=>b.match.score-a.match.score||a.p.title.localeCompare(b.p.title,locale)).slice(0,4).map(item=>item.p):[];
      this.suggestionCandidates=matches.map(p=>p.handle);
      note.textContent=!source?this.t('suggest_choose'):this.selected.length>=4?this.t('limit'):matches.length?this.t('suggest_hint'):this.t('suggest_empty');
      list.innerHTML=matches.map(p=>`<li>${photo(p)}<div><strong>${escape(p.title)}</strong><small>${escape(this.money(p))} · ${escape(format(p.gauge))} / 10 cm · ${escape(format(p.meters))} m / ${escape(format(p.weight))} g</small></div><button type="button" class="button button--secondary" data-suggest-add="${escape(p.handle)}" ${this.selected.length>=4?'disabled':''} aria-label="${escape(this.t('add')+': '+p.title)}">${escape(this.t('add'))}</button></li>`).join('');
    }
  }
  class YarnSimilarProducts extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.start = () => {
        this.owner = document.querySelector('yarn-product-features');
        if (!this.owner?.ready) return;
        document.removeEventListener('kickis:yarn-features-ready',this.start);
        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(entries=>{if(entries.some(e=>e.isIntersecting)){this.observer.disconnect();this.render();}},{rootMargin:'500px'});
          this.observer.observe(this);
        } else this.render();
      };
      document.addEventListener('kickis:yarn-features-ready',this.start);this.start();
      this.onclick=event=>{
        const button=event.target.closest('button');
        if(button?.hasAttribute('data-similar-retry'))this.render();
      };
    }
    disconnectedCallback() {this.observer?.disconnect();document.removeEventListener('kickis:yarn-features-ready',this.start);this.ready=false;}
    async render() {
      const status=this.querySelector('[data-yarn-similar-status]'),grid=this.querySelector('[data-yarn-similar-grid]'),owner=this.owner;
      if(!owner||this.busy)return;this.busy=true;status.textContent=owner.t('loading');
      try {
        const products=await owner.load();
        const matches=Core.recommendations(owner.current,products);
        status.textContent='';
        this.hidden=!matches.length;
        grid.innerHTML=matches.map(p=>{
          const facts=[p.gauge ? format(p.gauge)+' / 10 cm' : '',p.meters ? format(p.meters)+' m' : '',p.weight ? format(p.weight)+' g' : ''].filter(Boolean).join(' · ');
          return `<article class="yarn-similar__card"><a href="${escape(productUrl(p))}">${photo(p)}<h3>${escape(p.title)}</h3></a><p>${escape(owner.money(p))}</p><p>${escape(p.fiber || p.materials.join(', '))}${facts?`<br>${escape(facts)}`:''}</p></article>`;
        }).join('');
      } catch {status.innerHTML=`${escape(owner.t('load_error'))} <button type="button" data-similar-retry>${escape(owner.t('retry'))}</button>`;}
      finally {this.busy=false;}
    }
  }
  customElements.define('yarn-product-features',YarnProductFeatures);
  customElements.define('yarn-similar-products',YarnSimilarProducts);
})();
